/**
 * Calm, inline notes about the photos just picked, shown at the top of the
 * photo section instead of browser alert()s.
 *
 * An alert() blocks the page and reads as an error box; most of these are not
 * the customer's doing at all (a photo still downloading from iCloud, a handle
 * the phone took back). The owner's rule: no pop-ups, no nervous wording.
 *
 * One note per kind. Notes about photos merge across picks, so two picks in
 * quick succession end up in one note rather than the second hiding the first.
 *
 * `names` are the photos' file names, kept only to tell photos apart (the same
 * photo failing in two picks counts once). They are never shown: the notes
 * give a count, because customers do not recognise "IMG_1009.jpeg".
 */

export type UploadNote =
  /** Read failed at add time; removed from the selection. */
  | { kind: "unreadable"; names: string[] }
  /**
   * To be picked again: a restored pick whose stored copy could not be read,
   * or a live handle found dead just before the upload.
   */
  | { kind: "repick"; names: string[] }
  /** Slow to read (iCloud on iOS); kept, and may still upload. */
  | { kind: "stalled"; names: string[] }
  /** A ready-made sentence (validation, non-image files). */
  | { kind: "message"; id: string; text: string };

export type UploadNoteKind = UploadNote["kind"];

export function noteKey(note: UploadNote): string {
  return note.kind === "message" ? `message:${note.id}` : note.kind;
}

/** Adds `note`, merging its photos into an existing note of the same kind. */
export function mergeNote(notes: UploadNote[], note: UploadNote): UploadNote[] {
  const key = noteKey(note);
  const index = notes.findIndex((n) => noteKey(n) === key);
  if (index === -1) return [...notes, note];
  const existing = notes[index];
  const merged: UploadNote =
    "names" in existing && "names" in note
      ? { ...note, names: [...new Set([...existing.names, ...note.names])] }
      : note;
  return notes.map((n, i) => (i === index ? merged : n));
}
